import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { ObgConfigFeAdaptiveClientCore } from "@obg/core.config";
import { ignoreNil, ConfigService } from "@obg/ngx.global";
import { DocumentRef, MessageEventService, WindowRef } from "@obg/ngx.global.evo";
import { RouterState, RouteSelector } from "@obg/ngx.router";
import { remove } from "lodash-es";
import { merge, Subscription } from "rxjs";
import { distinctUntilChanged, pairwise, startWith, tap } from "rxjs/operators";

import { ScrollToTopDetail, SportsbookLayoutScrollId } from "./scroll-to-top.model";

@Injectable({
	providedIn: "root",
})
export class ScrollToTopService {
	private data$$: Subscription | undefined;
	private scrollDetails: ScrollToTopDetail[] = [];
	private readonly isMfeSbEnabled: boolean | undefined;

	constructor(
		private store: Store<RouterState>,
		private windowRef: WindowRef,
		private documentRef: DocumentRef,
		private messageEventService: MessageEventService,
		private configService: ConfigService<ObgConfigFeAdaptiveClientCore>
	) {
		this.isMfeSbEnabled = this.getIsSBB2BMfeEnabled();
	}

	register(scrollDetail: ScrollToTopDetail) {
		this.scrollDetails.push(scrollDetail);

		if (this.data$$) {
			return;
		}

		const routeChange$ = this.store.select(RouteSelector.getCurrent).pipe(
			ignoreNil(),
			distinctUntilChanged((prev, current) => prev?.pathName === current?.pathName),
			tap(() => this.triggerScroll(this.scrollDetails.filter((detail) => detail.routeChange)))
		);

		const queryChange$ = this.store.select(RouteSelector.getQueryParams).pipe(
			startWith(undefined),
			pairwise<Record<string, string> | undefined>(),
			tap(([prev, current]) => {
				const details = this.scrollDetails
					.filter((detail) => detail.queryChange && detail.queryParams)
					.filter((detail) =>
						detail.queryParams!.some((param) => (current && current[param]) !== (prev && prev[param]))
					);
				this.triggerScroll(details);
			})
		);

		this.data$$ = merge(routeChange$, queryChange$).subscribe();
	}

	unregister(id: string) {
		remove(this.scrollDetails as never, (element: ScrollToTopDetail) => element.id === id);

		if (!this.scrollDetails.length && this.data$$) {
			this.data$$.unsubscribe();
			this.data$$ = undefined;
		}
	}

	triggerCommandScroll(duration?: number) {
		const details = this.scrollDetails.filter((detail) => detail.commandTrigger);
		this.triggerScroll(details, duration);

		const isSportsbookScroll = details.some(
			(detail) => detail.id === SportsbookLayoutScrollId.Mfe || detail.id === SportsbookLayoutScrollId.Iframe
		);
		if (isSportsbookScroll) {
			this.triggerSportsbookScroll();
		}
	}

	private triggerSportsbookScroll(): void {
		if (this.isMfeSbEnabled) {
			this.windowRef.native.dispatchEvent(
				new CustomEvent("sb-xp-sportsbook-scroll-to-top-v1", {
					detail: {},
				})
			);
		} else {
			const iframe = this.documentRef.native.querySelector(
				"obg-sportsbook-iframe-container > iframe"
			) as HTMLIFrameElement | null;
			if (iframe?.contentWindow) {
				this.messageEventService.postMessage({ type: "scrollToTop" }, iframe.contentWindow);
			} else {
				console.warn("Sportsbook iframe not available");
			}
		}
	}

	private triggerScroll(details: ScrollToTopDetail[], duration?: number) {
		for (const detail of details) {
			detail.element.scrollTo({ top: 0, duration });
		}
	}

	private getIsSBB2BMfeEnabled() {
		return this.configService.get((config) => config.core.experiment.isSBB2BMfeEnabled);
	}
}
